import { useContext, useMemo, useRef, useSyncExternalStore } from 'react';
import { useInterval } from '@elfsight/lib-utils';
import { storageContext, withPostfix, withPrefix } from './storage-provider';
import type { StorageOperations } from './types';
import { storageOperations } from './utils/storage-operations';

export function useStorage<T>(
  key: string,
  initialValue: T,
  expiresIn?: number
): [T | undefined, StorageOperations<T>] {
  const { name, storageDriver, widgetId } = useContext(storageContext);

  if (!storageDriver) {
    throw new Error('Storage is not initialized');
  }

  const prefixedKey = withPrefix(withPostfix(key, widgetId), name);

  const operationsRef = useRef(
    storageOperations<T>(prefixedKey, storageDriver)
  );

  const syncedRawValue = useSyncExternalStore(subscribe, () =>
    storageDriver.getItem(prefixedKey)
  );

  const finalValue = useMemo(() => {
    const storedValue = operationsRef.current.get(expiresIn);
    if (storedValue === null || storedValue === undefined) {
      return initialValue;
    }
    return storedValue;

    // watch syncedRawValue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncedRawValue, initialValue, expiresIn]);

  useInterval(() => {
    if (operationsRef.current.isExpired(expiresIn)) {
      window.dispatchEvent(new StorageEvent('storage'));
    }
  }, 60000);

  const finalOperations = useMemo(
    () => ({
      ...operationsRef.current,
      set: (value: T) => {
        operationsRef.current.set(value);
        window.dispatchEvent(new StorageEvent('storage'));
      }
    }),
    []
  );

  return [finalValue, finalOperations];
}

const subscribe = (callback: () => void): (() => void) => {
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener('storage', callback);
  };
};
